<template>
    <div component="NavigationMenu" :class="{ small }">
        <template v-for="(item, index) in menuItems" :key="index">
            <router-link
                class="item outline"
                v-if="item.route"
                :icon="item.icon"
                :to="{ name: item.route }"
                :disabled="item.disabled"
                @click="$emit('hide')"
            >
                {{ item.label }}

                <span
                    v-if="item.status"
                    class="status"
                    :class="[monitoring.overall ?? 'unknown', { pill: monitoring.problems }]"
                    :title="monitoringTitle"
                >
                    <span v-if="monitoring.problems" class="count">{{ monitoring.problems }}</span>
                </span>
            </router-link>

            <div class="item"
                 v-if="item.emits"
                 :data-icon="item.icon"
                 @click="$emit(item.emits)"
            >
                {{ item.label }}
            </div>
        </template>
    </div>
</template>

<script>
import { defineComponent } from 'vue';
import { useAuthStore, useMonitoringStore } from '@stores/';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';

const MONITORING_INTERVAL = 60000;

export default defineComponent({
    name: 'NavigationMenu',

    setup() {
        const { t } = useI18n();
        const authStore = useAuthStore();
        const monitoringStore = useMonitoringStore();
        const router = useRouter();

        return { t, authStore, monitoringStore, router };
    },

    props: {
        small: {
            type: Boolean,
            default: false
        }
    },

    data: () => ({
        userSidebar: false,
        monitoringTimer: null,
        monitoring: {
            overall: null,
            problems: 0
        },
        menuItems: [
            {
                label: 'Monitoring',
                icon: 'monitor_heart',
                route: 'Monitoring',
                status: true
            },
            {
                label: 'Zákazníci',
                icon: 'group',
                route: 'Customers'
                // disabled: true
            },
            {
                label: 'Veľkoobchodné akcie',
                icon: 'bookmarks',
                route: 'Promotions'
            },
            {
                label: 'Bannery',
                icon: 'filter',
                route: 'Banners'
                // disabled: true
            },
            {
                label: 'Popupy',
                icon: 'speaker_notes',
                route: 'Popups'
                // disabled: true
            },
            {
                label: 'Newsletter',
                icon: 'forward_to_inbox',
                route: 'Newsletters'
                // disabled: true
            },
            {
                label: 'Produkty',
                icon: 'inventory_2',
                route: 'Products'
            },
            {
                label: 'Kategórie',
                icon: 'share',
                route: 'Categories'
            },

            {
                label: 'Manažovanie cien',
                icon: 'sell',
                route: 'PriceManagement'
            },

            {
                label: 'Množstevné zľavy',
                icon: 'percent',
                route: 'QuantityDiscounts'
            },

            {
                label: 'Nulové zásoby',
                icon: 'block',
                route: 'ZeroStock',
            },

            {
                label: 'Termíny a limity objednávok',
                icon: 'manage_history',
                route: 'OrderLimits'
                // disabled: true
            },

            {
                label: 'Sviatky',
                icon: 'event',
                route: 'Holidays'
                // disabled: true
            },

            {
                label: 'Odhlásiť sa',
                icon: 'power_settings_new',
                route: null,
                emits: 'logout'
                // disabled: true
            }
        ]
    }),

    created() {
        this.fetchMonitoring();
        this.monitoringTimer = setInterval(() => this.fetchMonitoring(), MONITORING_INTERVAL);
    },

    beforeUnmount() {
        clearInterval(this.monitoringTimer);
    },

    computed: {
        monitoringTitle() {
            return {
                ok: 'Všetko beží',
                warning: 'Otvorené chyby',
                critical: 'Niečo nebeží'
            }[this.monitoring.overall] ?? 'Stav sa nepodarilo načítať';
        }
    },

    methods: {
        // Len suma do bocneho panela - detaily su v sekcii Monitoring.
        async fetchMonitoring() {
            try {
                const status = await this.monitoringStore.fetchStatus();

                this.monitoring = {
                    overall: status.overall,
                    problems: status.checks.filter(check => check.state === 'late').length
                        + status.open_incidents_total
                };
            } catch {
                this.monitoring = { overall: null, problems: 0 };
            }
        }
    }
});
</script>

<style lang="scss" scoped>
@use '@scss/mixins';
@use '@scss/variables';
@use "sass:color";

[component='NavigationMenu'] {
    overflow: hidden;
    display: flex;
    flex-direction: column;
    background-color: rgba(black, 30%);

    &.small {
        .item {
            padding-left: 12px;
            font-size: 0;
            gap: 0;

            // Text zmizne, bodka musi ostat - inak by zbaleny panel
            // o probleme mlcal.
            .status {
                margin-left: 4px;

                .count {
                    display: none;
                }
            }
        }
    }

    .status {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 4px;
        margin-left: auto;
        min-width: 10px;
        height: 10px;
        border-radius: 5px;
        font-size: 11px;
        font-weight: bold;
        line-height: 1;
        color: variables.$white;

        // S poctom problemov sa z bodky stane pilulka s cislom.
        &.pill {
            height: 18px;
            min-width: 18px;
            border-radius: 9px;
        }

        &.ok {
            background: #2e7d32;
        }

        &.warning {
            background: #ed6c02;
        }

        &.critical {
            background: #c62828;
        }

        &.unknown {
            background: #9e9e9e;
        }

        .count {
            padding: 0 5px;
        }
    }

    .item {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        gap: 8px;
        color: variables.$goldLight;
        font-size: 15px;
        font-weight: bold;
        padding: 10px;
        border-bottom: 1px solid rgba(variables.$gold, 24%);
        cursor: pointer;
        transition: 0.3s;
        white-space: nowrap;
        height: 50px;

        &[disabled] {
            pointer-events: none;
            opacity: 0.5;
        }

        &:hover {
            background-color: color.mix(variables.$primary, variables.$black, 30%);
        }

        &.router-link-active,
        .router-link-exact-active {
            background-color: color.mix(variables.$primary, variables.$black, 40%);
            color: variables.$secondaryDark;
        }
    }
}
</style>
